// src/pages/finance-portal/tp-policies/TpPolicyForm.js
//
// Create / view / edit a Transfer Pricing Policy. Single page
// handles new / edit / read-only display with workflow buttons (activate/expire).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box, Typography, Paper, Button, TextField, IconButton, Card, Chip, Alert,
    MenuItem, Select, FormControl, InputLabel, CircularProgress, Skeleton,
    InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from 'notistack';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    DRAFT:      { bg: '#f1f5f9', color: '#64748b' },
    ACTIVE:     { bg: '#dcfce7', color: '#166534' },
    SUPERSEDED: { bg: '#e0e7ff', color: '#4338ca' },
    EXPIRED:    { bg: '#fecaca', color: '#991b1b' },
};

const METHOD_OPTS = [
    { value: 'CUP', label: 'Comparable Uncontrolled Price (CUP)' },
    { value: 'RESALE_PRICE', label: 'Resale Price' },
    { value: 'COST_PLUS', label: 'Cost Plus' },
    { value: 'TNMM', label: 'Transactional Net Margin (TNMM)' },
    { value: 'PROFIT_SPLIT', label: 'Profit Split' },
    { value: 'OTHER', label: 'Other' },
];

const TXN_TYPE_OPTS = [
    { value: 'GOODS', label: 'Goods' },
    { value: 'SERVICES', label: 'Services' },
    { value: 'INTANGIBLES', label: 'Intangibles' },
    { value: 'FINANCING', label: 'Financing' },
    { value: 'COST_SHARING', label: 'Cost Sharing' },
    { value: 'OTHER', label: 'Other' },
];

const initial = {
    name: '',
    description: '',
    from_entity_id: '',
    to_entity_id: '',
    transaction_type: 'SERVICES',
    method: 'COST_PLUS',
    target_pct: '',
    arm_length_low_pct: '',
    arm_length_high_pct: '',
    cost_base_description: '',
    currency: 'USD',
    effective_from: '',
    effective_to: '',
    next_review_date: '',
    methodology_notes: '',
    benchmarking_summary: '',
};

export default function TpPolicyForm() {
    const navigate = useNavigate();
    const { policyId } = useParams();
    const isEdit = !!policyId;
    const { enqueueSnackbar } = useSnackbar();

    const [data, setData] = useState({ ...initial });
    const [policy, setPolicy] = useState(null);
    const [entities, setEntities] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const fetchEntities = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/finance/entities',
                { params: { per_page: 200 } });
            if (res.data?.success) setEntities(res.data.data || []);
            else if (Array.isArray(res.data?.data)) setEntities(res.data.data);
        } catch { /* silent */ }
    }, []);

    const fetchPolicy = useCallback(async () => {
        if (!isEdit) return;
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/api/finance/tp/policies/${policyId}`);
            if (res.data.success) {
                const p = res.data.data;
                setPolicy(p);
                setData({
                    name: p.name || '',
                    description: p.description || '',
                    from_entity_id: p.from_entity_id || '',
                    to_entity_id: p.to_entity_id || '',
                    transaction_type: p.transaction_type || 'SERVICES',
                    method: p.method || 'COST_PLUS',
                    target_pct: p.target_pct != null ? String(p.target_pct) : '',
                    arm_length_low_pct: p.arm_length_low_pct != null ? String(p.arm_length_low_pct) : '',
                    arm_length_high_pct: p.arm_length_high_pct != null ? String(p.arm_length_high_pct) : '',
                    cost_base_description: p.cost_base_description || '',
                    currency: p.currency || 'USD',
                    effective_from: p.effective_from || '',
                    effective_to: p.effective_to || '',
                    next_review_date: p.next_review_date || '',
                    methodology_notes: p.methodology_notes || '',
                    benchmarking_summary: p.benchmarking_summary || '',
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load policy');
        } finally {
            setLoading(false);
        }
    }, [isEdit, policyId]);

    useEffect(() => { fetchEntities(); fetchPolicy(); }, [fetchEntities, fetchPolicy]);

    const update = (k, v) => {
        setData(prev => ({ ...prev, [k]: v }));
        if (errors[k]) setErrors(prev => { const n = { ...prev }; delete n[k]; return n; });
    };

    const isReadOnly = policy && (policy.status === 'SUPERSEDED' || policy.status === 'EXPIRED');

    const validateLocal = () => {
        const e = {};
        if (!data.name?.trim()) e.name = 'Required';
        if (!data.from_entity_id) e.from_entity_id = 'Required';
        if (!data.to_entity_id) e.to_entity_id = 'Required';
        if (data.from_entity_id && data.to_entity_id
            && parseInt(data.from_entity_id) === parseInt(data.to_entity_id)) {
            e.to_entity_id = 'Must be different from "from" entity';
        }
        const t = parseFloat(data.target_pct);
        const lo = parseFloat(data.arm_length_low_pct);
        const hi = parseFloat(data.arm_length_high_pct);
        if (isNaN(t)) e.target_pct = 'Required';
        if (isNaN(lo)) e.arm_length_low_pct = 'Required';
        if (isNaN(hi)) e.arm_length_high_pct = 'Required';
        if (!isNaN(lo) && !isNaN(hi) && hi < lo) e.arm_length_high_pct = 'Must be ≥ low';
        if (!isNaN(lo) && !isNaN(hi) && !isNaN(t) && (t < lo || t > hi)) {
            e.target_pct = `Must be within [${lo}, ${hi}]`;
        }
        if (!data.effective_from) e.effective_from = 'Required';
        if (data.effective_to && data.effective_from && data.effective_to < data.effective_from) {
            e.effective_to = 'Must be on or after effective_from';
        }
        return e;
    };

    const handleSave = async () => {
        const localErrors = validateLocal();
        if (Object.keys(localErrors).length > 0) {
            setErrors(localErrors);
            enqueueSnackbar('Fix the highlighted errors', { variant: 'warning' });
            return;
        }
        setSaving(true);
        setErrors({});
        try {
            const payload = {
                name: data.name.trim(),
                description: data.description?.trim() || null,
                from_entity_id: parseInt(data.from_entity_id),
                to_entity_id: parseInt(data.to_entity_id),
                transaction_type: data.transaction_type,
                method: data.method,
                target_pct: parseFloat(data.target_pct),
                arm_length_low_pct: parseFloat(data.arm_length_low_pct),
                arm_length_high_pct: parseFloat(data.arm_length_high_pct),
                cost_base_description: data.cost_base_description?.trim() || null,
                currency: (data.currency || 'USD').toUpperCase(),
                effective_from: data.effective_from,
                effective_to: data.effective_to || null,
                next_review_date: data.next_review_date || null,
                methodology_notes: data.methodology_notes?.trim() || null,
                benchmarking_summary: data.benchmarking_summary?.trim() || null,
            };
            let id = policyId;
            if (isEdit) {
                await axiosInstance.put(`/api/finance/tp/policies/${policyId}`, payload);
                enqueueSnackbar('Policy updated', { variant: 'success' });
            } else {
                const res = await axiosInstance.post('/api/finance/tp/policies', payload);
                id = res.data?.data?.id;
                enqueueSnackbar('Policy created (DRAFT)', { variant: 'success' });
            }
            navigate(`/finance-portal/tp-policies/${id}`);
        } catch (err) {
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && typeof serverErrors === 'object') {
                setErrors(serverErrors);
                enqueueSnackbar('Validation failed', { variant: 'warning' });
            } else {
                enqueueSnackbar(err.response?.data?.message || 'Failed to save', { variant: 'error' });
            }
        } finally {
            setSaving(false);
        }
    };

    const action = async (path) => {
        try {
            const res = await axiosInstance.post(`/api/finance/tp/policies/${policyId}/${path}`);
            enqueueSnackbar(res.data?.message || 'Done', { variant: 'success' });
            fetchPolicy();
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const deletePolicy = async () => {
        if (!window.confirm('Delete this DRAFT policy?')) return;
        try {
            await axiosInstance.delete(`/api/finance/tp/policies/${policyId}`);
            enqueueSnackbar('Policy deleted', { variant: 'success' });
            navigate('/finance-portal/tp-policies');
        } catch (err) {
            enqueueSnackbar(err.response?.data?.message || 'Failed', { variant: 'error' });
        }
    };

    const fieldSx = { fontFamily, fontSize: '0.85rem', borderRadius: '10px' };
    const labelSx = { fontFamily, fontSize: '0.85rem' };

    if (loading) return <Box sx={{ p: 4, fontFamily }}><Skeleton height={400} /></Box>;

    const sStyle = policy && (STATUS_COLORS[policy.status] || STATUS_COLORS.DRAFT);

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <IconButton onClick={() => navigate('/finance-portal/tp-policies')}
                                sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.15)',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' } }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>
                            Transfer Pricing Policy
                        </Typography>
                        <Typography variant="h5" sx={{ fontFamily, fontWeight: 800, color: 'white' }}>
                            {isEdit && policy ? policy.name : 'New Policy'}
                        </Typography>
                        {policy && (
                            <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.85rem', mt: 0.25 }}>
                                {policy.policy_number} · {policy.from_entity_name} → {policy.to_entity_name}
                            </Typography>
                        )}
                    </Box>
                </Box>
                {policy && (
                    <Box sx={{ mt: 1 }}>
                        <Chip label={policy.status} size="small"
                              sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem',
                                    bgcolor: sStyle.bg, color: sStyle.color }} />
                    </Box>
                )}
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1100, mx: 'auto' }}>
                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                {/* Action bar */}
                {isEdit && policy && (
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {policy.status === 'DRAFT' && (
                            <Button startIcon={<CheckCircleIcon />} variant="contained" size="small"
                                    onClick={() => action('activate')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                          borderRadius: '10px', bgcolor: '#16a34a' }}>
                                Activate
                            </Button>
                        )}
                        {policy.status === 'ACTIVE' && (
                            <Button startIcon={<EventBusyIcon />} variant="outlined" size="small"
                                    onClick={() => action('expire')}
                                    sx={{ fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
                                Expire
                            </Button>
                        )}
                        {policy.status === 'DRAFT' && (
                            <Button startIcon={<DeleteIcon />} variant="text" size="small" onClick={deletePolicy}
                                    sx={{ fontFamily, textTransform: 'none', color: '#94a3b8' }}>
                                Delete
                            </Button>
                        )}
                    </Box>
                )}

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Identity
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Name *" size="small"
                                   value={data.name}
                                   onChange={e => update('name', e.target.value)}
                                   error={!!errors.name} helperText={errors.name || ' '}
                                   InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <TextField label="Currency" size="small"
                                   value={data.currency}
                                   onChange={e => update('currency', e.target.value.toUpperCase())}
                                   inputProps={{ maxLength: 3 }}
                                   InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                                   InputLabelProps={{ sx: labelSx }} />
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>From Entity *</InputLabel>
                            <Select value={data.from_entity_id} label="From Entity *"
                                    onChange={e => update('from_entity_id', e.target.value)}
                                    disabled={isReadOnly}
                                    error={!!errors.from_entity_id}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {entities.map(en =>
                                    <MenuItem key={en.id} value={en.id} sx={{ fontFamily }}>
                                        {en.name}{en.code ? ` (${en.code})` : ''}
                                    </MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>To Entity *</InputLabel>
                            <Select value={data.to_entity_id} label="To Entity *"
                                    onChange={e => update('to_entity_id', e.target.value)}
                                    disabled={isReadOnly}
                                    error={!!errors.to_entity_id}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {entities.map(en =>
                                    <MenuItem key={en.id} value={en.id} sx={{ fontFamily }}>
                                        {en.name}{en.code ? ` (${en.code})` : ''}
                                    </MenuItem>)}
                            </Select>
                        </FormControl>
                    </Box>
                    <TextField label="Description" size="small" fullWidth multiline rows={2} sx={{ mt: 1.5 }}
                               value={data.description}
                               onChange={e => update('description', e.target.value)}
                               InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                               InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Method & Pricing
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.5 }}>
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Transaction Type</InputLabel>
                            <Select value={data.transaction_type} label="Transaction Type"
                                    onChange={e => update('transaction_type', e.target.value)}
                                    disabled={isReadOnly}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {TXN_TYPE_OPTS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl size="small">
                            <InputLabel sx={labelSx}>Method</InputLabel>
                            <Select value={data.method} label="Method"
                                    onChange={e => update('method', e.target.value)}
                                    disabled={isReadOnly}
                                    sx={{ fontFamily, borderRadius: '10px' }}>
                                {METHOD_OPTS.map(o =>
                                    <MenuItem key={o.value} value={o.value} sx={{ fontFamily }}>{o.label}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField label="Target % *" size="small" type="number"
                                   value={data.target_pct}
                                   onChange={e => update('target_pct', e.target.value)}
                                   error={!!errors.target_pct}
                                   helperText={errors.target_pct || 'e.g. 5.0 for 5% markup'}
                                   InputProps={{
                                       endAdornment: <InputAdornment position="end"
                                           sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                       sx: fieldSx, readOnly: isReadOnly,
                                   }}
                                   InputLabelProps={{ sx: labelSx }}
                                   FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        <Box sx={{ display: 'flex', gap: 1.5 }}>
                            <TextField label="Range Low % *" size="small" type="number" fullWidth
                                       value={data.arm_length_low_pct}
                                       onChange={e => update('arm_length_low_pct', e.target.value)}
                                       error={!!errors.arm_length_low_pct}
                                       helperText={errors.arm_length_low_pct || 'IQR low'}
                                       InputProps={{
                                           endAdornment: <InputAdornment position="end"
                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                           sx: fieldSx, readOnly: isReadOnly,
                                       }}
                                       InputLabelProps={{ sx: labelSx }}
                                       FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                            <TextField label="Range High % *" size="small" type="number" fullWidth
                                       value={data.arm_length_high_pct}
                                       onChange={e => update('arm_length_high_pct', e.target.value)}
                                       error={!!errors.arm_length_high_pct}
                                       helperText={errors.arm_length_high_pct || 'IQR high'}
                                       InputProps={{
                                           endAdornment: <InputAdornment position="end"
                                               sx={{ '& .MuiTypography-root': { fontFamily, fontSize: '0.85rem' } }}>%</InputAdornment>,
                                           sx: fieldSx, readOnly: isReadOnly,
                                       }}
                                       InputLabelProps={{ sx: labelSx }}
                                       FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        </Box>
                    </Box>
                    <TextField label="Cost Base Description" size="small" fullWidth sx={{ mt: 1.5 }}
                               placeholder="e.g. Local entity's fully-loaded labor + overhead"
                               value={data.cost_base_description}
                               onChange={e => update('cost_base_description', e.target.value)}
                               InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                               InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Effectiveness
                    </Typography>
                    <Box sx={{ display: 'grid',
                              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                        <TextField label="Effective From *" type="date" size="small"
                                   value={data.effective_from}
                                   onChange={e => update('effective_from', e.target.value)}
                                   error={!!errors.effective_from}
                                   helperText={errors.effective_from || ' '}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx, readOnly: isReadOnly }} />
                        <TextField label="Effective To" type="date" size="small"
                                   value={data.effective_to}
                                   onChange={e => update('effective_to', e.target.value)}
                                   error={!!errors.effective_to}
                                   helperText={errors.effective_to || 'Optional'}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                                   FormHelperTextProps={{ sx: { fontFamily, fontSize: '0.7rem' } }} />
                        <TextField label="Next Review" type="date" size="small"
                                   value={data.next_review_date}
                                   onChange={e => update('next_review_date', e.target.value)}
                                   InputLabelProps={{ shrink: true, sx: labelSx }}
                                   InputProps={{ sx: fieldSx, readOnly: isReadOnly }} />
                    </Box>
                </Card>

                <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', p: 3, mb: 2 }}>
                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '0.95rem', mb: 2 }}>
                        Documentation
                    </Typography>
                    <TextField label="Methodology Notes" size="small" fullWidth multiline rows={3}
                               sx={{ mb: 1.5 }}
                               value={data.methodology_notes}
                               onChange={e => update('methodology_notes', e.target.value)}
                               InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                               InputLabelProps={{ sx: labelSx }} />
                    <TextField label="Benchmarking Summary" size="small" fullWidth multiline rows={3}
                               value={data.benchmarking_summary}
                               onChange={e => update('benchmarking_summary', e.target.value)}
                               InputProps={{ sx: fieldSx, readOnly: isReadOnly }}
                               InputLabelProps={{ sx: labelSx }} />
                </Card>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                    <Button onClick={() => navigate('/finance-portal/tp-policies')}
                            sx={{ fontFamily, textTransform: 'none', color: '#64748b' }}>
                        {isReadOnly ? 'Back' : 'Cancel'}
                    </Button>
                    {!isReadOnly && (
                        <Button variant="contained" onClick={handleSave} disabled={saving}
                                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                sx={{ fontFamily, fontWeight: 600, textTransform: 'none',
                                      borderRadius: '10px', bgcolor: ACCENT,
                                      '&:hover': { bgcolor: '#16a34a' } }}>
                            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Policy'}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
}
